import type { BrushMode, Level } from './types';

/** Clamp a value between min and max (inclusive). */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Maximum height delta applied to the tile at the exact center of a brush stroke, at full intensity. */
export const BRUSH_STRENGTH = 1;

/**
 * Brush radius in tiles, at the narrowest setting. Deliberately 1 tile: at
 * this radius `computeBrushWeight`'s circular falloff only touches the
 * center cell plus its 4 orthogonal neighbors (diagonals are `sqrt(2)`
 * tiles away, outside the radius), with those neighbors weighted far
 * lighter than the center. This gives players a surgical tool for
 * correcting a single isolated spike or dip without disturbing an already-
 * flat neighborhood — the wider minimum (2 tiles) used to nudge correct
 * neighbors just enough to create a new, smaller error one ring further
 * out, producing an ever-worsening checkerboard pattern when chased
 * repeatedly.
 */
export function minBrushSize(): number {
  return 0.8;
}

/** Brush radius in tiles, at the widest setting ("half the field"). */
export function maxBrushSize(level: Level): number {
  return Math.max(minBrushSize(), level.size / 2);
}

/** Brush radius is continuous (no discrete steps), only clamped to the level's valid range. */
export function clampBrushSize(size: number, level: Level): number {
  const min = minBrushSize();
  const max = maxBrushSize(level);
  return Math.min(max, Math.max(min, size));
}

/**
 * The hard height limit: half the grid size (`size / 2`). Reaching this
 * height on any block immediately fails the attempt. The halved limit
 * produces a flatter visual aspect ratio and tighter gameplay margin.
 */
export function hardHeightLimit(level: Level): number {
  return level.size / 2;
}

/**
 * Gaussian falloff weight for a tile at `distance` tiles from the brush
 * center, given the brush's `radius`. Weight is 1 at the center, decays
 * smoothly outward, and is exactly 0 beyond the radius so the brush has a
 * clearly bounded circular area of effect.
 */
export function computeBrushWeight(distance: number, radius: number): number {
  if (radius <= 0 || distance > radius) {
    return 0;
  }
  const sigma = radius / 2;
  return Math.exp(-(distance * distance) / (2 * sigma * sigma));
}

/** Result of applying a brush stroke to a height grid. */
export type BrushResult = {
  /** Updated height array (new reference if anything changed). */
  heights: number[];
  /** Total absolute height change applied this stroke. */
  appliedChange: number;
  /** Which boundary was hit, or null if the stroke is safe. */
  failedBoundary: 'floor' | 'ceiling' | null;
  /** Indices of tiles that were modified (non-zero weight). */
  affectedIndices: number[];
};

/**
 * Pure brush application: takes heights + brush parameters, returns BrushResult.
 * Does NOT read or produce GameState — the reducer composes this into state transitions.
 *
 * The caller must pass a copy of the heights array if they need to preserve the original.
 */
export function applyBrushToGrid(
  heights: number[],
  gridSize: number,
  x: number,
  y: number,
  mode: BrushMode,
  radius: number,
  intensity: number,
  limit: number,
): BrushResult {
  const strength = clamp(intensity, 0, 1) * BRUSH_STRENGTH;
  const delta = mode === 'lift' ? strength : -strength;

  const minX = Math.max(0, Math.floor(x - radius));
  const maxX = Math.min(gridSize - 1, Math.ceil(x + radius));
  const minY = Math.max(0, Math.floor(y - radius));
  const maxY = Math.min(gridSize - 1, Math.ceil(y + radius));

  const affectedIndices: number[] = [];
  let appliedChange = 0;
  let failedBoundary: 'floor' | 'ceiling' | null = null;

  for (let cy = minY; cy <= maxY; cy += 1) {
    for (let cx = minX; cx <= maxX; cx += 1) {
      const dx = cx - x;
      const dy = cy - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const weight = computeBrushWeight(distance, radius);
      if (weight <= 0) {
        continue;
      }
      const index = cy * gridSize + cx;
      affectedIndices.push(index);
      const before = heights[index];
      const after = clamp(before + delta * weight, 0, limit);
      heights[index] = after;
      appliedChange += Math.abs(after - before);
      if (after <= 0 || after >= limit) {
        failedBoundary = mode === 'lift' ? 'ceiling' : 'floor';
      }
    }
  }

  return { heights, appliedChange, failedBoundary, affectedIndices };
}

/**
 * Detects tile pairs where heights "cross through" equality during a brush
 * stroke. A crossing occurs when the sign of (heightA - heightB) changes
 * between before and after, or the after-difference is exactly zero.
 *
 * Only checks pairs where at least one tile is in `affectedIndices`.
 * Returns deduplicated set of all tile indices that should flash.
 */
export function detectHeightCrossings(
  beforeHeights: number[],
  afterHeights: number[],
  affectedIndices: number[],
  size: number,
): Set<number> {
  const result = new Set<number>();
  const checked = new Set<string>();

  for (const idx of affectedIndices) {
    const x = idx % size;
    const y = Math.floor(idx / size);

    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || nx >= size || ny < 0 || ny >= size) continue;

        const nIdx = ny * size + nx;
        const key = Math.min(idx, nIdx) + ',' + Math.max(idx, nIdx);
        if (checked.has(key)) continue;
        checked.add(key);

        const diffBefore = beforeHeights[idx] - beforeHeights[nIdx];
        const diffAfter = afterHeights[idx] - afterHeights[nIdx];

        if (diffBefore !== 0 && diffAfter !== 0 && Math.sign(diffBefore) !== Math.sign(diffAfter)) {
          result.add(idx);
          result.add(nIdx);
        }
      }
    }
  }

  return result;
}
